import axios from 'axios'
import { API_URL } from './config'

// Get current origin for callback URLs
const getOrigin = () => {
  return typeof window !== 'undefined' ? window.location.origin : ''
}

// Authentication services
export const authService = {
  // Verify invite code
  verifyInviteCode: async (email, code) => {
    const response = await axios.post(`${API_URL}/api/auth/verify-code`, {
      email,
      code,
    })
    return response.data
  },
  // Register a new user
  register: async (userData) => {
    // Add callback URL for email verification
    const callbackUrl = `${getOrigin()}/verificar-email?token={{token}}`
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, {
        ...userData,
        callbackUrl,
      })
      return response.data
    } catch (error) {
      // Capturar e formatar erros de validação
      if (error.response?.data?.details) {
        const details = error.response.data.details
        const passwordErrors = details.filter((d) =>
          d.path.includes('password'),
        )
        if (passwordErrors.length > 0) {
          // Extrair e combinar mensagens de erro relacionadas à senha
          const errorMessage = passwordErrors.map((e) => e.message).join(', ')
          throw {
            response: {
              data: {
                message: errorMessage,
              },
            },
          }
        }
      }
      throw error
    }
  },
  // Register user data to CRM
  registerCRM: async (crmData) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/register-crm`,
        crmData,
      )
      return response.data
    } catch (error) {
      // Log error but don't throw - this is a secondary operation
      console.error('Failed to register user in CRM:', error)
    }
  },
  // Verify email with token
  verifyEmail: async (token) => {
    const response = await axios.post(`${API_URL}/api/auth/verify-email`, {
      token,
    })
    // If successful, store tokens and user data
    if (response.data.accessToken) {
      localStorage.setItem('accessToken', response.data.accessToken)
      localStorage.setItem('refreshToken', response.data.refreshToken)
      localStorage.setItem('user', JSON.stringify(response.data.user))
      // Dispatch custom event to notify components about auth change
      window.dispatchEvent(new Event('auth-change'))
    }
    return response.data
  },
  // Login with email and password
  login: async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password,
    })
    // If successful, store tokens and user data
    if (response.data.accessToken) {
      localStorage.setItem('accessToken', response.data.accessToken)
      localStorage.setItem('refreshToken', response.data.refreshToken)
      localStorage.setItem('user', JSON.stringify(response.data.user))
      // Dispatch custom event to notify components about auth change
      window.dispatchEvent(new Event('auth-change'))
    }
    return response.data
  },
  // Request magic link for passwordless login
  requestMagicLink: async (email) => {
    const callbackUrl = `${getOrigin()}/magic-link?token={{token}}`
    const response = await axios.post(
      `${API_URL}/api/auth/request-magic-link`,
      {
        email,
        callbackUrl,
      },
    )
    return response.data
  },
  // Verify magic link token
  verifyMagicLink: async (token) => {
    const response = await axios.get(
      `${API_URL}/api/auth/verify-magic-link/${token}`,
    )
    // If successful, store tokens and user data
    if (response.data.accessToken) {
      localStorage.setItem('accessToken', response.data.accessToken)
      localStorage.setItem('refreshToken', response.data.refreshToken)
      localStorage.setItem('user', JSON.stringify(response.data.user))
      // Dispatch custom event to notify components about auth change
      window.dispatchEvent(new Event('auth-change'))
    }
    return response.data
  },
  // Refresh access token using refresh token
  refreshToken: async () => {
    const refreshToken = localStorage.getItem('refreshToken')
    if (!refreshToken) return false
    try {
      // Using fetch directly to avoid interceptor loop
      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      })
      if (response.ok) {
        const data = await response.json()
        localStorage.setItem('accessToken', data.accessToken)
        return true
      }
      return false
    } catch (error) {
      console.error('Failed to refresh token:', error)
      return false
    }
  },
  // Get current user profile
  getProfile: async () => {
    const accessToken = localStorage.getItem('accessToken')
    const response = await axios.get(`${API_URL}/api/user/profile`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    // Save profile data to localStorage for future use
    if (response.data) {
      localStorage.setItem(
        'profileData',
        JSON.stringify({
          ...response.data,
          timestamp: Date.now(),
        }),
      )
    }
    return response.data
  },
  // Update user profile
  updateProfile: async (profileData) => {
    const accessToken = localStorage.getItem('accessToken')
    const response = await axios.put(
      `${API_URL}/api/user/profile`,
      profileData,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    // Update local storage with new user data if response includes user
    if (response.data.user) {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
      const updatedUser = { ...currentUser, ...response.data.user }
      localStorage.setItem('user', JSON.stringify(updatedUser))
      // Also update the profile data cache
      localStorage.setItem(
        'profileData',
        JSON.stringify({
          ...response.data.user,
          timestamp: Date.now(),
        }),
      )
      // Dispatch custom event to notify components about auth change
      window.dispatchEvent(new Event('auth-change'))
    }
    return response
  },
  // Change password
  changePassword: async (currentPassword, newPassword) => {
    const accessToken = localStorage.getItem('accessToken')
    try {
      const response = await axios.post(
        `${API_URL}/api/user/change-password`,
        {
          currentPassword,
          newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
      return response.data
    } catch (error) {
      // Capturar e formatar erros de validação
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors
        const passwordErrors = errors.filter((e) =>
          e.path.includes('newPassword'),
        )
        if (passwordErrors.length > 0) {
          // Extrair e combinar mensagens de erro relacionadas à senha
          const errorMessage = passwordErrors.map((e) => e.message).join(', ')
          throw {
            response: {
              data: {
                message: errorMessage,
              },
            },
          }
        }
      }
      throw error
    }
  },
  // Request password reset (forgot password)
  requestPasswordReset: async (email) => {
    const callbackUrl = `${getOrigin()}/nova-senha?token={{token}}`
    const response = await axios.post(`${API_URL}/api/user/forgot-password`, {
      email,
      callbackUrl,
    })
    return response.data
  },
  // Reset password with token
  resetPassword: async (token, newPassword) => {
    try {
      const response = await axios.post(`${API_URL}/api/user/reset-password`, {
        token,
        newPassword,
      })
      return response.data
    } catch (error) {
      // Capturar e formatar erros de validação
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors
        const passwordErrors = errors.filter((e) =>
          e.path.includes('newPassword'),
        )
        if (passwordErrors.length > 0) {
          // Extrair e combinar mensagens de erro relacionadas à senha
          const errorMessage = passwordErrors.map((e) => e.message).join(', ')
          throw {
            response: {
              data: {
                message: errorMessage,
              },
            },
          }
        }
      }
      throw error
    }
  },
  // Logout - clear local storage and revoke tokens on server
  logout: async (revokeAll = false) => {
    const refreshToken = localStorage.getItem('refreshToken')
    const accessToken = localStorage.getItem('accessToken')
    if (refreshToken) {
      try {
        // Call the logout endpoint to revoke tokens
        await axios.post(
          `${API_URL}/api/auth/logout`,
          {
            refreshToken,
            revokeAll,
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        )
      } catch (error) {
        console.error('Error during logout:', error)
      }
    }
    // Clear local storage
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    localStorage.removeItem('profileData')
    // Dispatch custom event to notify components about auth change
    window.dispatchEvent(new Event('auth-change'))
    // Also dispatch storage event to notify other tabs
    window.dispatchEvent(new Event('storage'))
  },
  // Check if user is authenticated
  isAuthenticated: () => {
    return !!localStorage.getItem('accessToken')
  },
  // Get current user from localStorage
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user')
    return userStr ? JSON.parse(userStr) : null
  },
}

// Export the API_URL for use in refreshToken
export const API_URL = 'https://brand-attribute-score-backend.replit.app'
